import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';

const UserDataForm = () => {
  const { submitUserData, user, checkUserData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    ageRange: '',
    gender: '',
    consents: {
      termsAndConditions: {
        accepted: false,
        acceptedAt: null
      },
      privacyPolicy: {
        accepted: false,
        acceptedAt: null
      },
      marketing: {
        accepted: false,
        acceptedAt: null
      }
    }
  });
  const [error, setError] = useState('');

  useEffect(() => {
    const checkExistingData = async () => {
      try {
        const hasData = await checkUserData();
        if (hasData) {
          // Si el usuario ya tiene datos, redirigir a la página principal
          navigate('/', { replace: true });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error al verificar datos del usuario:', error);
        setError('Error al verificar los datos del usuario');
        setLoading(false);
      }
    };

    if (user) {
      checkExistingData();
    }
  }, [user, navigate, checkUserData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consents.termsAndConditions.accepted || !formData.consents.privacyPolicy.accepted) {
      setError('Debes aceptar los términos y condiciones y la política de privacidad');
      return;
    }

    // Verificar que tenemos el número de teléfono del usuario autenticado
    const phoneNumber = user?.providerData[0]?.phoneNumber;
    console.log('Usuario autenticado:', user);
    console.log('Datos del proveedor:', user?.providerData);
    console.log('Número de teléfono encontrado:', phoneNumber);

    if (!phoneNumber) {
      console.error('No se encontró el número de teléfono del usuario');
      setError('Error: No se pudo obtener el número de teléfono');
      return;
    }

    const now = new Date().toISOString();
    
    // Preparar los datos según el modelo User.js
    const updatedFormData = {
      firebaseId: user.uid,
      email: formData.email,
      name: formData.name,
      phone: phoneNumber,
      ageRange: formData.ageRange,
      gender: formData.gender,
      consents: {
        termsAndConditions: {
          accepted: true,
          acceptedAt: now
        },
        privacyPolicy: {
          accepted: true,
          acceptedAt: now
        },
        marketing: {
          accepted: Boolean(formData.consents.marketing.accepted),
          acceptedAt: formData.consents.marketing.accepted ? now : null
        }
      }
    };

    try {
      console.log('Datos a enviar:', JSON.stringify(updatedFormData, null, 2));
      const response = await submitUserData(updatedFormData);
      console.log('Respuesta del servidor:', response);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error completo:', error);
      console.error('Detalles del error:', error.response?.data);
      setError(error.response?.data?.message || 'Error al guardar los datos. Por favor, inténtalo de nuevo.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
    });
  };

  const handleConsentChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      consents: {
        ...prev.consents,
        [name]: {
          ...prev.consents[name],
          accepted: checked
        }
      }
    }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <PageContainer>
      <GradientTopBar>
        <Title>
          <MainTitle>El Rasca y Gana</MainTitle>
          <SubTitle>de Pamplonews</SubTitle>
        </Title>
      </GradientTopBar>
      <FormContainer>
        <FormTitle>Completa tu perfil</FormTitle>
        <FormDescription>
          Para brindarte una mejor experiencia, necesitamos algunos datos básicos.
        </FormDescription>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Nombre</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Tu nombre"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Rango de edad</Label>
            <Select
              name="ageRange"
              value={formData.ageRange}
              onChange={handleChange}
              required
              defaultValue=""
            >
              <option value="" disabled>Selecciona tu rango de edad</option>
              <option value="18-24">18-24 años</option>
              <option value="25-34">25-34 años</option>
              <option value="35-44">35-44 años</option>
              <option value="45-54">45-54 años</option>
              <option value="55+">55+ años</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Género</Label>
            <Select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
              defaultValue=""
            >
              <option value="" disabled>Selecciona tu género</option>
              <option value="male">Masculino</option>
              <option value="female">Femenino</option>
              <option value="not_specified">Prefiero no especificar</option>
            </Select>
          </FormGroup>

          <LegalSection>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="termsAndConditions"
                name="termsAndConditions"
                checked={formData.consents.termsAndConditions.accepted}
                onChange={handleConsentChange}
                required
              />
              <CheckboxLabel htmlFor="termsAndConditions">
                Acepto los <LegalLink href="https://lawwwing.com/documents/c01c2ae1-7fb6-4c08-8e9a-4a921c72dc84/terms-and-conditions/es/" target="_blank">términos y condiciones</LegalLink> del servicio
              </CheckboxLabel>
            </CheckboxGroup>
            
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="privacyPolicy"
                name="privacyPolicy"
                checked={formData.consents.privacyPolicy.accepted}
                onChange={handleConsentChange}
                required
              />
              <CheckboxLabel htmlFor="privacyPolicy">
                He leído y acepto la <LegalLink href="https://lawwwing.com/documents/c01c2ae1-7fb6-4c08-8e9a-4a921c72dc84/privacy-policy/es/" target="_blank">política de privacidad</LegalLink>
              </CheckboxLabel>
            </CheckboxGroup>

            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="marketing"
                name="marketing"
                checked={formData.consents.marketing.accepted}
                onChange={handleConsentChange}
              />
              <CheckboxLabel htmlFor="marketing">
              Acepto recibir ofertas exclusivas y promociones especiales 🚀
              </CheckboxLabel>
            </CheckboxGroup>
          </LegalSection>

          <LegalText>
            Al registrarte, aceptas que tratemos tus datos personales de acuerdo con nuestra política de privacidad. 
            Tus datos serán tratados con la finalidad de gestionar tu participación en el juego y enviarte 
            comunicaciones relacionadas con el mismo.
          </LegalText>

          <ActionButton type="submit">Registrarme</ActionButton>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Form>
      </FormContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url('/images/sort.jpeg') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  isolation: isolate;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: -1;
    position: fixed;
  }
`;

const GradientTopBar = styled.header`
  position: relative;
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;
  margin-bottom: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Title = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const MainTitle = styled.h1`
  font-family: 'Fredoka One', sans-serif;
  font-size: 2.1rem;
  font-weight: 700;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 3px white;
  margin: 0;
  margin-top: 1.8rem;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const SubTitle = styled.h2`
  font-family: 'Fredoka One', sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 1px white;
  margin: 0;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem 2rem;
  min-height: calc(100vh - 200px);
`;

const Form = styled.form`
  background: rgba(88, 28, 135, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 215, 0, 0.2);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 400px;
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  color: #ffd700;
  font-size: 1.8rem;
  text-align: center;
  margin: 1rem 0;
  font-family: 'Fredoka One', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  color: rgba(255, 215, 0, 0.8);
  display: block;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  backdrop-filter: blur(5px);
  font-size: 18px;
  -webkit-text-size-adjust: 100%;
  touch-action: manipulation;
  transition: all 0.3s ease;
  height: 55px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 0 0 15px rgba(255, 215, 0, 0.2);
    transform: translateY(-1px);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(88, 28, 135, 0.6);
  border-radius: 4px;
  color: white;
  font-size: 18px;
  -webkit-text-size-adjust: 100%;
  touch-action: manipulation;
  cursor: pointer;
  appearance: none;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  position: relative;
  height: 55px;
  
  /* Flecha personalizada */
  background-image: linear-gradient(45deg, transparent 50%, rgba(255, 215, 0, 0.8) 50%),
                    linear-gradient(135deg, rgba(255, 215, 0, 0.8) 50%, transparent 50%);
  background-position: 
    calc(100% - 20px) calc(1em + 2px),
    calc(100% - 15px) calc(1em + 2px);
  background-size: 
    5px 5px,
    5px 5px;
  background-repeat: no-repeat;

  &:focus {
    outline: none;
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 0 0 15px rgba(255, 215, 0, 0.2);
    transform: translateY(-1px);
  }

  /* Estilo para las opciones */
  option {
    background: rgba(88, 28, 135, 0.95);
    color: white;
    padding: 1rem;
    font-size: 1.1rem;

    &:hover {
      background: rgba(255, 215, 0, 0.2);
    }
  }

  /* Placeholder */
  &:invalid {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const FormDescription = styled.p`
  font-family: 'Roboto', sans-serif;
  color: white;
  text-align: center;
  margin: 0.5rem 0;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 179, 71, 0.2) 100%);
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  letter-spacing: 0.5px;
  margin-top: 1.5rem;
  height: 55px;

  &:hover {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(255, 179, 71, 0.3) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const LegalSection = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  appearance: none;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  position: relative;
  margin-top: 0.2rem;

  &:checked {
    background: rgba(255, 215, 0, 0.2);
    border-color: rgba(255, 215, 0, 0.5);
    
    &::after {
      content: '✓';
      position: absolute;
      color: #ffd700;
      font-size: 16px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &:hover {
    border-color: rgba(255, 215, 0, 0.5);
  }
`;

const CheckboxLabel = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  line-height: 1.4;
  cursor: pointer;
`;

const LegalLink = styled.a`
  color: #ffd700;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    color: white;
    text-decoration: underline;
  }
`;

const LegalText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  line-height: 1.5;
  margin-top: 1.5rem;
  padding: 0 0.5rem;
  text-align: justify;
`;

export default UserDataForm; 